import React, { useEffect, useState, useCallback } from 'react'
import { API, Auth } from 'aws-amplify';
import { Alert, Button, Spinner, Card, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getAuthToken } from '../../utils/AuthUtils';
import GenericGridComponent from '../../GenericGridComponent/GenericGridComponent';
import './styles.scss';
import SurveyStatusDropDown from './SurveyStatusDropDown/SurveyStatusDropDown';
import EditSurveySubmissionModal from './EditSurveySubmissionModal/EditSurveySubmissionModal';
import { exportToCSV } from '../../utils/exportUtils';

const SurveySubmissionTab = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [error, setError] = useState(null);
    const [errorDisplayStatus, setErrorDisplayStatus] = useState(false);
    const [selectedSubmissionStatus, setSelectedSubmissionStatus] = useState("");
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [isAdminUser, setIsAdminUser] = useState(false);
    const [isSalesUser, setIsSalesUser] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedSubmissionRecord, setSelectedSubmissionRecord] = useState(null);
    const [showEditSubmissionModal, setShowEditSubmissionModal] = useState(false)

    const actions = (row) => {
        return (
            <div className="action-buttons">
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-edit-${row.participantId}`}>Edit this survey submission</Tooltip>}
                >
                    <span>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={!isAdminUser}
                            className="action-btn"
                            onClick={() => handleEditSumbissionRecord(row)}
                            aria-label="Edit this survey submission"
                        >
                            <i className="fa fa-edit" />
                        </Button>
                    </span>
                </OverlayTrigger>
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id={`tooltip-download-original-${row.participantId}`}>Download the original submitted file</Tooltip>}
                >
                    <span>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={!isAdminUser && !isSalesUser}
                            className="action-btn"
                            onClick={() => getSubmisionFile(row.fileKey)}
                            aria-label="Download the original submitted file"
                        >
                            <i className="fa fa-download" />
                        </Button>
                    </span>
                </OverlayTrigger>
                {row.processedFileKey && (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tooltip-download-processed-${row.participantId}`}>Download the processed/accepted file</Tooltip>}
                    >
                        <span>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={!isAdminUser}
                                className="action-btn"
                                onClick={() => getProcessedFile(row.processedFileKey)}
                                aria-label="Download the processed/accepted file"
                            >
                                <i className="fa fa-file-alt" />
                            </Button>
                        </span>
                    </OverlayTrigger>
                )}
                {row.validationErrorCsv && (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tooltip-export-errors-${row.participantId}`}>Export validation errors as CSV</Tooltip>}
                    >
                        <span>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="action-btn"
                                onClick={() => handleExportValidationErrors(row.validationErrorCsv)}
                                aria-label="Export validation errors as CSV"
                            >
                                <i className="fa fa-file-csv" />
                            </Button>
                        </span>
                    </OverlayTrigger>
                )}
            </div>
        )
    }

    const handleEditSumbissionRecord = (submissionRecord) => {
        setSelectedSubmissionRecord(submissionRecord);
        setShowEditSubmissionModal(true);
    }

    const formatDate = (date) => {
        return new Date(date).toLocaleString();
    }

    const handleDownloadFile = (url, fileName = undefined) => {
        const link = document.createElement('a');
        link.href = url;
        if (fileName) {
            link.download = fileName;
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const submissionStatusFormatter = (submissionStatus) => {
        switch (submissionStatus) {
            case "SUBMITTED_SUCCESSFULLY":
                return "Submitted Successfully";
            case "SUBMITTED_WITH_ERRORS":
                return "Submitted With Errors";
            case "READY_FOR_REVIEW":
                return "Ready For Review";
            case "REVIEWED":
                return "Reviewed";
            default:
                return "All"
        }
    }

    const handleExportValidationErrors = (validationErrorsCsv) => {
        const blob = new Blob([validationErrorsCsv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        handleDownloadFile(url, "ECS_Survey_Validation.csv");
    }

    const columns = [{
        "key": "participantName",
        "name": "Participant Name",
        "sortable": true,
        "filterable": true
    }, {
        "key": "submitterEmail",
        "name": "Submitter Email",
        "sortable": true,
        "filterable": true
    }, {
        "key": "submissionDate",
        "name": "Submission Date",
        "sortable": true,
        "filterable": true,
        "formatter": (props) => {
            return (
                formatDate(props.row.submissionDate)
            );
        }
    }, {
        "key": "submissionStatus",
        "name": "Submission Status",
        "sortable": true,
        "filterable": true,
        "formatter": (props) => {
            return (
                submissionStatusFormatter(props.row.submissionStatus)
            );
        }
    }, {
        "key": "version",
        "name": "Submission Version",
        "sortable": true,
        "filterable": true
    }, {
        "key": "actions",
        "sortable": false,
        "filterable": false,
        "formatter": (props) => {
            return (
                actions(props.row)
            );
        }
    }]

    const getSubmisionFile = async (fileKey) => {
        const authToken = await getAuthToken();
        const path = `/restricted/survey/files?fileKey=${encodeURIComponent(fileKey)}`;
        API.get('AnnualCompensations', path, {
            headers: {
                'Token': authToken
            }
        }).then((response) => {
            handleDownloadFile(response.url);
        }).catch((error) => {
            if (error?.response?.data?.message) {
                console.log(error.response.data.message);
            } else {
                console.log(error);
                console.log("An error occurred while retrieving the submission file. Please try again later.");
            }
            setError("An error occurred downloading the submission file");
            setErrorDisplayStatus(true);
        })
    }

    const getProcessedFile = async (processedFileKey) => {
        const authToken = await getAuthToken();
        const path = `/restricted/survey/processed-files?fileKey=${encodeURIComponent(processedFileKey)}`;
        API.get('AnnualCompensations', path, {
            headers: {
                'Token': authToken
            }
        }).then((response) => {
            handleDownloadFile(response.url);
        }).catch((error) => {
            if (error?.response?.data?.message) {
                console.log(error.response.data.message);
            } else {
                console.log(error);
                console.log("An error occurred while retrieving the processed file. Please try again later.");
            }
            setError("An error occurred downloading the processed file");
            setErrorDisplayStatus(true);
        })
    }

    const getSubmissionData = useCallback(async (submissionStatus) => {
        setLoading(true);
        setErrorDisplayStatus(false);
        setError("");

        const authToken = await getAuthToken();

        const path = `/restricted/survey/submissions?surveyYear=${selectedYear}&status=${submissionStatus}`

        API.get('AnnualCompensations', path, {
            headers: {
                "Content-Type": "application/json",
                "Token": authToken
            }
        }).then(response => {
            setData(response);
            setLoading(false);
        }).catch(error => {
            console.log(error);
            setError("Error occurred retrieving submission data");
            setErrorDisplayStatus(true);
            setLoading(false);
        });
    }, [selectedYear]);

    const createDataRows = (data) => {
        return data.map(row => {
            return {
                "Participant Name": row.participantName,
                "Participant ID": row.participantId,
                "Submission Status": row.submissionStatus,
                "Submitter Email": row.submitterEmail,
                "Submission Date": row.submissionDate,
                "Submission Version": row.version
            }
        });
    }

    const getAdminUser = async () => {
        const user = await Auth.currentAuthenticatedUser();
        const groups = user.signInUserSession.accessToken.payload["cognito:groups"];
        setIsAdminUser(groups.includes("FWC_Admin"));
    }

    const getSalesUser = async () => {
        const user = await Auth.currentAuthenticatedUser();
        const groups = user.signInUserSession.accessToken.payload["cognito:groups"];
        setIsSalesUser(groups.includes("Sales"));
    }

    useEffect(() => {
        let mounted = true;
        if (mounted) {
            setData([]);
            setLoading(false);
            setErrorDisplayStatus(false);
            setError("");
            getSubmissionData(selectedSubmissionStatus);
            getAdminUser();
            getSalesUser();
            setCurrentPage(1);
        }
        return () => mounted = false;
    }, [selectedSubmissionStatus, selectedYear, getSubmissionData]);

    const renderContent = () => {
        if (!isAdminUser && !isSalesUser) {
            return (
                <Alert variant="warning">
                    <i className="fa fa-lock me-2" />Administrative or Sales access required to view submissions
                </Alert>
            );
        }

        if (loading) {
            return (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading submissions...</p>
                </div>
            );
        }

        if (data.length === 0) {
            let friendlyStatusMsg = '';
            switch (selectedSubmissionStatus) {
                case 'SUBMITTED_WITH_ERRORS':
                    friendlyStatusMsg = 'No submissions with errors were found for the selected year.';
                    break;
                case 'SUBMITTED_SUCCESSFULLY':
                    friendlyStatusMsg = 'No successful submissions were found for the selected year.';
                    break;
                case 'READY_FOR_REVIEW':
                    friendlyStatusMsg = 'No submissions ready for review were found for the selected year.';
                    break;
                case 'REVIEWED':
                    friendlyStatusMsg = 'No reviewed submissions were found for the selected year.';
                    break;
                default:
                    friendlyStatusMsg = 'No submissions found for the selected year.';
            }
            return (
                <Alert variant="info" className="text-center">
                    <i className="fa fa-info-circle me-2" />
                    {friendlyStatusMsg}
                </Alert>
            );
        }

        return (
            <GenericGridComponent
                columns={columns}
                data={data}
                enableGridToCSV={true}
                useCustomExportMethod={true}
                customExportMethod={() => exportToCSV(data, createDataRows, "Survey_Submissions", "Survey Submissions")}
                fileName={"Survey_Submissions"}
                itemsName={"Survey Submissions"}
                selectedCurrentPage={currentPage}
                onCurrentPageSelect={(page) => setCurrentPage(page)}
            />
        );
    };

    return (
        <div className='survey-submissions-container'>
            {selectedSubmissionRecord && (
                <EditSurveySubmissionModal
                    data={selectedSubmissionRecord}
                    show={showEditSubmissionModal}
                    onClose={() => {
                        setShowEditSubmissionModal(false);
                        setSelectedSubmissionRecord(null);
                    }}
                    onSubmissionUpdated={() => getSubmissionData(selectedSubmissionStatus)}
                />
            )}

            <Card className="survey-submissions-card">
                <Card.Header className="filters-row">
                    <div className="filters-inner">
                        <Form.Group controlId="surveyYear" className="mb-0 d-flex flex-column align-items-start">
                            <Form.Label className="mb-1" style={{ fontWeight: 500 }}>Year</Form.Label>
                            <Form.Select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                                style={{ width: "8rem" }}
                            >
                                <option value={new Date().getFullYear().toString()}>{new Date().getFullYear()}</option>
                                <option value={(new Date().getFullYear() - 1).toString()}>{new Date().getFullYear() - 1}</option>
                                <option value={(new Date().getFullYear() - 2).toString()}>{new Date().getFullYear() - 2}</option>
                            </Form.Select>
                        </Form.Group>
                        <div className="d-flex flex-column align-items-start">
                            <SurveyStatusDropDown
                                submissionStatus={selectedSubmissionStatus}
                                onStatusChange={(status) => setSelectedSubmissionStatus(status)}
                            />
                        </div>
                    </div>
                </Card.Header>

                <Card.Body>
                    {errorDisplayStatus && (
                        <Alert variant="danger" dismissible onClose={() => setErrorDisplayStatus(false)}>
                            {error}
                        </Alert>
                    )}
                    {renderContent()}
                </Card.Body>
            </Card>
        </div>
    )
}

export default SurveySubmissionTab;